import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@apollo/client/react';
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Alert,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import InvoicePrintContent from '../../components/Invoices/InvoicePrintContent';
import { GET_ORDER, GetOrderData, GetOrderVariables } from '../../graphql/queries/orders';
import { useInvoiceNumberMap } from '../../hooks/useInvoiceNumberMap';
import AccessDenied from '../Utility/AccessDenied';
import { useOwnOrderAccess } from '../../hooks/useOwnOrderAccess';

const InvoiceDetails = () => {
  const { orderId } = useParams<{ orderId?: string }>();
  const { invoiceNumberMap, ready: invoiceMapReady } = useInvoiceNumberMap();

  const { data, loading, error } = useQuery<GetOrderData, GetOrderVariables>(GET_ORDER, {
    variables: { id: orderId || '', idType: 'DATABASE_ID' },
    skip: !orderId,
    fetchPolicy: 'cache-and-network',
  });

  const order = data?.order;
  const { denied } = useOwnOrderAccess(order, { loading: loading && !order, error });
  const invoiceNumber = order ? invoiceNumberMap.get(order.databaseId) : undefined;
  const orderNumberLabel = order
    ? `Order #${order.orderNumber ?? order.databaseId}`
    : undefined;

  document.title = invoiceNumber
    ? `Invoice ${invoiceNumber} | NGK Tours`
    : orderNumberLabel
      ? `${orderNumberLabel} | NGK Tours`
      : 'Invoice Detail | NGK Tours';

  const printInvoice = () => {
    window.print();
  };

  if (!orderId) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Invoices" breadcrumbItem="Invoice Detail" />
          <Alert color="info" className="mb-3">
            Select an invoice from the{' '}
            <Link to="/invoices-list">Invoice List</Link> to view details.
          </Alert>
        </Container>
      </div>
    );
  }

  if (denied) {
    return <AccessDenied />;
  }

  if (loading && !order) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Invoices" breadcrumbItem="Invoice Detail" />
          <div className="text-center py-5">
            <Spinner color="primary" />
            <p className="mt-2 text-muted">Loading invoice...</p>
          </div>
        </Container>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Invoices" breadcrumbItem="Invoice Detail" />
          <Alert color="danger">
            {(error as { graphQLErrors?: Array<{ message?: string }> }).graphQLErrors?.[0]
              ?.message || error.message}
          </Alert>
          <Link to="/invoices-list" className="btn btn-secondary mt-2">
            Back to Invoice List
          </Link>
        </Container>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Invoices" breadcrumbItem="Invoice Detail" />
          <Alert color="info">Invoice not found.</Alert>
          <Link to="/invoices-list" className="btn btn-secondary mt-2">
            Back to Invoice List
          </Link>
        </Container>
      </div>
    );
  }

  const invoiceHeading = invoiceNumber
    ? invoiceNumber
    : !invoiceMapReady
      ? '…'
      : '—';

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title={invoiceNumber ? `Invoice ${invoiceNumber}` : orderNumberLabel || 'Invoice'}
            breadcrumbItem="Invoice Detail"
          />

          <Row>
            <Col lg="12">
              <Card>
                <CardBody>
                  <InvoicePrintContent order={order} invoiceNumber={invoiceHeading} />
                  <div className="d-print-none mt-4">
                    <div className="float-end">
                      <Link
                        to="#"
                        onClick={printInvoice}
                        className="btn btn-success me-2"
                      >
                        <i className="fa fa-print" />
                      </Link>
                      <Link to="#" className="btn btn-primary w-md">
                        Send
                      </Link>
                    </div>
                  </div>
                  <div className="d-print-none mt-4 clearfix">
                    <Link to="/invoices-list" className="btn btn-secondary">
                      Back to Invoice List
                    </Link>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default InvoiceDetails;
